<template>
  <div class="merge-layout">
    <b-modal :active.sync="isDrawingModalActive" has-modal-card @close="resize" :width="`76%`" class="modal">
      <div v-if="!imgLoading" @reset="resize" class="visual-drawing">
        <img class="visual-sample" ref="visual-sample-modal" :src="img" />
        <canvas-drawing
          :active.sync="active"
          :shape="shape"
          :width="widthModal"
          :height="heightModal"
          :coordinatesInputs="coordinates"
          @edit="editData"
          @data="getData"
          :is-modal="true"
          :key="isDrawingModalActive"
        ></canvas-drawing>
      </div>
      <loader v-else></loader>
      <div v-show="!imgLoading" class="field is-grouped is-buttons-under-canvas">
        <p class="control">
          <a class="button" @click="drawShape(shape)">{{ $t(`button.draw.${shape}`) }}</a>
        </p>
      </div>
    </b-modal>

    <div v-if="!loading">
      <div class="top">
        <h2>
          <slot name="type">{{ items.type }}</slot>
        </h2>
        <actions aria-role="list" position="is-bottom-left">
          <template>
            <slot name="actions"></slot>
          </template>
        </actions>
      </div>
      <div class="columns">
        <div class="column is-3">
          <slot name="column-left">
            <b-input
              type="textarea"
              v-if="editingDescription"
              v-model="description"
              @keyup.native="keyupDescriptionEditing"
              @blur="stopDescriptionEditing"
              ref="descriptionEdit"
            />
            <p v-else class="description" @click="startDescriptionEditing" ref="pageDescription">
              <span v-html="items.description"></span>
              <span class="icon is-small">
                <i class="fas fa-pencil-alt"></i>
              </span>
            </p>
          </slot>
        </div>
        <div class="column is-5 column-center">
          <slot name="column-center">
            <parametrs-list
              @draw="draw"
              :items.sync="items.parameters"
              :parameters="properties.parameters"
              :disabledDrawButtons="imgLoading"
            ></parametrs-list>
          </slot>
        </div>
        <div class="column is-4">
          <slot name="column-right">
            <loader v-if="imgLoading"></loader>
            <img v-else :src="img" />
            <slot name="upload"></slot>
          </slot>
        </div>
      </div>

      <div class="columns">
        <div class="column is-3">
          <edit-button :editOptions="['save']" @click="save">
            <template>
              <span class="text">{{ $t('button.save') }}</span>
            </template>
          </edit-button>
        </div>
        <div class="column is-5">
          <slot name="test">
            <button class="button test" @click="test">{{ $t('lists.test') }}</button>
            <loader v-if="testLoading" class="test"></loader>
            <span v-else>{{ status }}</span>
          </slot>
        </div>
        <div class="column is-4"></div>
      </div>
    </div>
    <loader v-else></loader>
  </div>
</template>

<script>
import Loader from '@/components/Loader'
import Actions from '@/components/Actions'
import EditButton from '@/components/EditButton'
import ParametrsList from '@/components/ParametrsList'
import CanvasDrawing from '@/components/CanvasDrawing'

export default {
  name: 'SettingsLayout',
  components: {
    Loader,
    Actions,
    EditButton,
    ParametrsList,
    CanvasDrawing
  },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    imgLoading: {
      type: Boolean,
      default: false
    },
    testLoading: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    items: {
      type: Object,
      default() {
        return {}
      }
    },
    properties: {
      type: Object,
      default() {
        return {}
      }
    },
    status: {
      type: String,
      default: ''
    },
    img: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      config: {},
      editingDescription: false,
      description: '',
      coordinates: [
        {
          x: 0,
          y: 0
        },
        {
          x: 0,
          y: 0
        }
      ],
      active: false,
      shape: 'rectangle',
      widthModal: null,
      heightModal: null,
      isDrawingModalActive: false,
      updateDrawItem: null
    }
  },
  methods: {
    test() {
      this.$emit('change', this.items)
      this.$emit('click:test')
    },
    save() {
      this.$emit('change', this.items)
      this.$emit('click:save')
    },
    startDescriptionEditing() {
      this.editingDescription = true
      this.description = this.items.description
      this.$nextTick(() => {
        this.$refs.descriptionEdit.focus()
      })
    },
    keyupDescriptionEditing(e) {
      if (e.ctrlKey && e.key === 'Enter') {
        this.stopDescriptionEditing()
      } else if (e.key === 'Escape') {
        this.cancelDescriptionEditing()
      }
    },
    stopDescriptionEditing() {
      this.editingDescription = false
      this.items.description = this.description
    },
    cancelDescriptionEditing() {
      this.editingDescription = false
    },
    draw(item) {
      this.isDrawingModalActive = true
      this.shape = item.shape
      this.updateDrawItem = item.key
      try {
        this.coordinates = this.items.parameters[item.key]
          .replace(/\s/g, '')
          .replace(/\),/g, ') ')
          .split(' ')
          .map(s =>
            s
              .replace(/\)\(/g, ') (')
              .split(' ')
              .map(d => {
                const xy = d
                  .replace(/\(/g, '')
                  .replace(/\)/g, '')
                  .split(',')
                return {
                  x: Number(xy[0]),
                  y: Number(xy[1])
                }
              })
          )
          .flat()
      } catch {
        this.$notify({
          message: this.$t('pipeline.wrong_coordinates_format'),
          type: 'danger'
        })
      }
    },
    drawShape(type) {
      this.active = true
      this.$emit('update:shape', type)
    },
    resize() {
      try {
        this.widthModal = this.$refs['visual-sample-modal'].clientWidth
        this.heightModal = this.$refs['visual-sample-modal'].clientHeight
      } catch (e) {
        this.widthModal = 100
        this.heightModal = 100
      }
    },
    getData({ coordinates, shape }) {
      if (this.updateDrawItem) {
        this.items.parameters[this.updateDrawItem] = coordinates
          .map(point => `(${parseFloat(point.x).toFixed(2)}, ${parseFloat(point.y).toFixed(2)})`)
          .join(' ')
          .replace(/(\([+-]?\d+(\.\d+)?, [+-]?\d+(\.\d+)?\) \([+-]?\d+(\.\d+)?, [+-]?\d+(\.\d+)?\))./g, '$1, ')
        this.$forceUpdate()
      }
      this.coordinates = coordinates
      this.$emit('update:shape', shape)
      this.$emit('edit-data', coordinates)
    },
    editData({ shape, coordinates }) {
      this.coordinates = coordinates
      this.$emit('update:shape', shape)
      this.$emit('edit-data', this.coordinates)
    }
  },
  mounted() {
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
  },
  destroyed() {
    window.removeEventListener('resize', this.resize)
  },
  watch: {
    coordinates: {
      immediate: true,
      handler(value) {
        this.coordinates = value
      }
    },
    isDrawingModalActive() {
      this.$nextTick(this.resize)
    }
  },
  model: {
    event: 'change'
  }
}
</script>

<style lang="scss">
.merge-layout {
  .visual-drawing {
    position: relative;
    width: 100%;
    .visual-sample {
      object-fit: contain;
      max-height: 80vh;
    }
    .canvas-container {
      position: absolute !important;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: 0;
    }
  }
  .field.is-grouped.is-buttons-under-canvas {
    .control {
      margin-left: auto;
      margin-right: auto;
    }
  }
  .top {
    display: flow-root;
    margin-bottom: 1.25rem;
    h2 {
      font-weight: bold;
      float: left;
      font-size: 150%;
    }
    .actions {
      float: right;
    }
  }
  .columns .column {
    .button.test {
      color: #3273dc;
      border-color: #3273dc;
      padding: 0rem 1.25rem;
      height: 1.25rem;
      margin-right: 1.25rem;
      font-weight: bold;
      float: left;
    }
    .v-spinner.test {
      float: left;
      height: 1.25rem;
      .v-clip {
        width: 1.25rem !important;
        height: 1.25rem !important;
      }
    }
  }
  .description {
    cursor: pointer;
    &:hover {
      .icon {
        display: inline;
      }
    }
    .icon {
      display: none;
      font-size: 0.8rem;
      margin-left: 0.5rem;
    }
  }
}
</style>
